import { SWRConfig, useSWRConfig } from 'swr';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import EducatorCourses from '@cont/EducatorProfile/EducatorCourses';
import SEO from '@seo/index';
import { fetchUserProfileData } from '@api/fetchers/profile';
import { fetchCoursesWithFilters } from '@api/fetchers/courses';
import useInfiniteFetch from '@hooks/useInfiniteFetch';
import { processCourse } from '@api/processors/course';
import { getIsLoggedIn } from '@selectors/auth';
import Layout from '@layout/index';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const courseMiddleware = (useSWRNext) => (key, fetcher, config) => {
  const swr = useSWRNext(key, fetcher, config);

  return {
    ...swr,
    data:
      swr.data?.map((page) => {
        if (page.processed) {
          return page;
        }

        return {
          ...page,
          processed: true,
          results:
            page.results?.map((course) =>
              processCourse({ ...course, ...(course.programme || {}) })
            ) || []
        };
      }) || []
  };
};

const useCoursesByFilters = (
  { userName },
  { filtersApplied, sortsApplied, offset },
  options = { fallbackData: undefined }
) => {
  const filterParams = {
    limit: 12,
    filters_applied: JSON.stringify(filtersApplied)
  };
  const key = `v1/search_v3/educators/${userName}/courses/v2/`;
  if (sortsApplied) filterParams.sorts_applied = JSON.stringify(sortsApplied);
  if (offset) filterParams.offset = offset;

  const {
    data: coursesByFilters,
    isValidating,
    ...rest
  } = useInfiniteFetch(
    key,
    { applyOffsetsAutomatically: false, params: filterParams },
    {
      use: [courseMiddleware],
      fallbackData: options.fallbackData,
      revalidateOnMount: options.revalidateOnMount
    }
  );
  return {
    key,
    isValidating,
    ...rest,
    coursesByFilters
  };
};

const getFiltersApplied = (query) => {
  return {
    topic: { id: query.topic || '' },
    show_only: { id: query.courses || '' },
    goal: { id: query.goal || '' },
    language: { id: query.language || '' },
    filter_by: { id: query.type || 'plus' }
  };
};

const Courses = ({
  profileInfo,
  fallbackCourses,
  filtersAppliedOnServer,
  authorID
}) => {
  const router = useRouter();
  const { cache } = useSWRConfig();

  const isLoggedIn = useSelector(getIsLoggedIn);

  const [filtersApplied, setFiltersApplied] = useState(filtersAppliedOnServer);
  const [sortsApplied, setSortsApplied] = useState(null);

  const {
    coursesByFilters,
    mutate: refetchData,
    isValidating,
    setSize
  } = useCoursesByFilters(
    { userName: authorID?.slice(1), type: '' },
    { filtersApplied: getFiltersApplied(router.query), sortsApplied },
    { fallbackData: [fallbackCourses], revalidateOnMount: false }
  );

  useEffect(() => {
    if (isLoggedIn) {
      cache.clear();
      setSize(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    const currentFilter = getFiltersApplied(router.query);

    if (!isEqual(currentFilter, filtersApplied)) {
      setFiltersApplied(currentFilter);
      cache.clear();
      setSize(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  return (
    <EducatorCourses
      authorID={authorID}
      filtersApplied={filtersApplied}
      profileInfo={profileInfo}
      setSortsApplied={setSortsApplied}
      filteredCoursesData={{
        coursesByFilters,
        updateCoursesListData: refetchData,
        isValidating,
        setSize
      }}
    />
  );
};

const CoursesPage = ({
  profileInfo,
  fallbackCourses,
  filtersAppliedOnServer
}) => {
  const router = useRouter();
  let { authorID } = router.query;
  if (!authorID.startsWith('@')) authorID = `@${authorID}`;
  authorID = escape(authorID);

  const { firstName, lastName, description } = profileInfo;
  const title = `${firstName} ${lastName} courses | Unacademy`;
  const url = `https://unacademy.com/${authorID}`;

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000
      }}
    >
      <SEO
        title={title}
        url={url}
        description={description}
        openGraph={{ title, description, url }}
      />
      <Courses
        profileInfo={profileInfo}
        fallbackCourses={fallbackCourses}
        filtersAppliedOnServer={filtersAppliedOnServer}
        authorID={authorID}
      />
    </SWRConfig>
  );
};

CoursesPage.pageInfo = {
  layoutOptions: {
    hideFooter: true
  },
  isMWebSupported: true
};

export const getStaticProps = async ({ params: query }) => {
  let userName = query.authorID;
  if (!userName) return { notFound: true };
  if (userName.startsWith('@')) userName = query.authorID?.slice(1);
  userName = escape(userName);

  const profileInfo = await fetchUserProfileData(userName);
  if (!profileInfo || profileInfo.errorCode)
    return { notFound: true, revalidate: 60 };

  const { isEducator, isExEducator, isModerator } = profileInfo;
  const isEducatorProfile = isEducator || isExEducator || isModerator;

  // if user is Learner we should redirect to learn page instead of showing page not found
  const isLearnerProfile = !isEducator && !isExEducator && !isModerator;

  if (isLearnerProfile)
    return {
      redirect: { destination: `/learner/@${userName}`, permanent: true }
    };

  if (!isEducatorProfile) return { notFound: true, revalidate: 60 };

  const filtersToApply = {
    topic: { id: '' },
    show_only: { id: '' },
    goal: { id: '' },
    language: { id: '' },
    filter_by: { id: 'plus' }
  };
  let fallbackCourses = await fetchCoursesWithFilters(userName, filtersToApply);

  if (!fallbackCourses?.results?.length) {
    // No plus courses for educator
    filtersToApply.filter_by.id = ''; // Reset plus course filter and have the backend return the default.
    fallbackCourses = await fetchCoursesWithFilters(userName, filtersToApply);
  }

  return {
    props: {
      profileInfo,
      fallbackCourses,
      filtersAppliedOnServer: filtersToApply
    },
    revalidate: 5 * 60
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default CoursesPage;

CoursesPage.propTypes = {
  profileInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  fallbackCourses: PropTypes.objectOf(PropTypes.any).isRequired,
  filtersAppliedOnServer: PropTypes.objectOf(PropTypes.any).isRequired
};
CoursesPage.Layout = Layout;
CoursesPage.Wrapper = PageReduxProvider;
